import { NoResults } from "client/Components";
import { GlobalSearchTermModel } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useMemo
} from "react";
import { ScopeLayout } from "../ScopeLayout";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import styles from "./RecentSearch.module.less";
import { Guid } from "client_legacy/ndaw/guid";
import { FrequentlyUsedAppList } from "client/Features/Layout/TitleBar/GlobalSearch/RecentSearchView/FrequentlyUsedAppList";
import { useFrequentlyUsedTiles } from "client/Features/Layout/FrequentlyUsedTiles/useFrequentlyUsedTiles";
import { FrequentlyUsedTileModel } from "client/Api/Workspace/workspace";
import { MAX_FREQUENTLY_USED_TILES, MAX_RECENT_SEARCH_RESULTS } from "client/Features/Layout/TitleBar/GlobalSearch/RecentSearchView/RecentSearchView";
import { ScopeSection } from "../ScopeSection";
import { GlobalSearchItemPersistedModel } from "./Persistence/models";
import { RecentlyOpenedView } from "./RecentlyOpened/RecentlyOpenedView";
import { TimeZone } from "client/DateAndTime/timezones";
import { RecentSearchListView } from "./RecentSearchListView/RecentSearchListView";

export type RecentSearchProps = {
	recentSearchQueries: readonly GlobalSearchTermModel[];
	recentSearchResults: readonly GlobalSearchItemPersistedModel[];
	isMobile: boolean;
	onSearch(searchTerm: string, version: Guid): void;
	onDeleteRecentSearchQueries(ids: readonly number[]): void;
	onRemoveRecentSearchResult: (item: GlobalSearchItemPersistedModel) => void;
	onClearRecentSearchResults: () => void;
	onInvokeFrequentlyUsedTile: (item: FrequentlyUsedTileModel) => void;
	timezone: TimeZone;
};

export const RecentSearch = (props: RecentSearchProps): ReactElement => {
	const {
		recentSearchQueries,
		recentSearchResults,
		isMobile,
		onSearch,
		onRemoveRecentSearchResult,
		onClearRecentSearchResults,
		onDeleteRecentSearchQueries,
		onInvokeFrequentlyUsedTile,
		timezone
	} = props;

	const [ items ] = useFrequentlyUsedTiles();

	const frequentlyUsedTiles = useMemo(
		(): readonly FrequentlyUsedTileModel[] | undefined => items
			? items.slice(0, MAX_FREQUENTLY_USED_TILES)
			: undefined,
		[items]
	);

	const recentSearchResultsToRender = useMemo(
		(): readonly GlobalSearchItemPersistedModel[] => recentSearchResults
			.slice(0, MAX_RECENT_SEARCH_RESULTS),
		[recentSearchResults]
	);

	const hasRecentSearchResults: boolean = recentSearchResultsToRender.length !== 0;
	const hasRecentSearchQueries: boolean = recentSearchQueries.length !== 0;

	const renderRecentSearchResults = (): ReactElement => {
		if (!hasRecentSearchResults && !hasRecentSearchQueries) {
			return (
				<NoResults
					trackingId="has-no-results"
					icon="magnifying-glass"
					title={localize("_EnterASearchTerm")}
					message={localize("_PleaseEnterASearchTermToBeginYourSearch")}
				/>
			);
		}

		return (
			<>
				{hasRecentSearchResults && (
					<RecentlyOpenedView
						recentSearchResults={recentSearchResultsToRender}
						navigationIndexStart={0}
						onRemove={onRemoveRecentSearchResult}
						onClear={onClearRecentSearchResults}
						isMobile={isMobile}
						timezone={timezone}
					/>
				)}
				{hasRecentSearchQueries && (
					<RecentSearchListView
						recentSearchQueries={recentSearchQueries}
						navigationIndexStart={recentSearchResultsToRender.length}
						onDelete={onDeleteRecentSearchQueries}
						onSearch={onSearch}
					/>
				)}
			</>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Results>
				{renderRecentSearchResults()}
				{(isNil(frequentlyUsedTiles) || frequentlyUsedTiles.length > 0) && (
					<ScopeSection
						title={localize("_FrequentlyUsedApps")}
						trackingId="frequently-used-apps"
					>
						<div className={styles.gridContainer}>
							<FrequentlyUsedAppList
								{...(isNotNil(frequentlyUsedTiles)
									? {
										status: "ready",
										items: frequentlyUsedTiles,
										onInvoke: onInvokeFrequentlyUsedTile
									}
									: {
										status: "loading"
									}
								)}
							/>
						</div>
					</ScopeSection>
				)}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};

RecentSearch.displayName = "RecentSearch";
